// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import xHeaderUpdate from "@guestsupply/App/Handlers/XNG/Header";
import { CustomAppState } from "@guestsupply/App/Store/Reducers";
import InfoAlert from "@guestsupply/XDL/Icons/InfoAlert";
import { getSystemColor } from "@guestsupply/XDL/Utilities";
import { parserOptions } from "@insite/client-framework/Common/BasicSelectors";
import { getCookie, setCookie } from "@insite/client-framework/Common/Cookies";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Icon, { IconPresentationProps, IconWrapper } from "@insite/mobius/Icon";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import parse from "html-react-parser";
import React, { FC, useEffect } from "react";
import { useDispatch } from "react-redux";

const enum fields {
    message = "message",
    backgroundColor = "backgroundColor",
    showCloseIcon = "showCloseIcon",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.message]: string;
        [fields.backgroundColor]: string;
        [fields.showCloseIcon]: boolean;
    };
}

type Props = OwnProps;

export interface GlobalBannerMessageStyles {
    container?: InjectableCss;
    wrapper?: InjectableCss;
    messageWrapper?: InjectableCss;
    closeClickable?: ClickablePresentationProps;
    closeIcon?: IconPresentationProps;
    infoIcon?: IconPresentationProps;
    message?: TypographyPresentationProps;
}

export const globalBannerMessageStyles: GlobalBannerMessageStyles = {};

const styles = globalBannerMessageStyles;

export const ShowTopBannerCookieName = "showTopBanner";

const GlobalBannerMessage: FC<Props> = ({ fields: { message, backgroundColor, showCloseIcon } }) => {
    const showTopBanner = getCookie(ShowTopBannerCookieName);
    const dispatch = useDispatch();

    const { showGlobalMessage } = useAppSelector(state => ({
        showGlobalMessage: (state as CustomAppState).xHeader.showGlobalMessage,
    }));

    useEffect(() => {
        if (!showTopBanner) {
            setCookie(ShowTopBannerCookieName, `${showGlobalMessage}`);
        }
    }, []);

    const onClose = () => {
        dispatch(xHeaderUpdate({ key: "showGlobalMessage", value: false }));
        setCookie(ShowTopBannerCookieName, "false");
    };

    if (!showGlobalMessage || !message?.length || showTopBanner === "false") {
        return null;
    }

    return (
        <StyledWrapper {...styles.wrapper} {...{ backgroundColor }} data-test-selector="global-message-widget">
            <StyledWrapper {...styles.messageWrapper}>
                <Icon src={InfoAlert} {...styles.infoIcon} />
                <Typography {...styles.message} data-test-selector="global_site_message">
                    {parse(message || "", parserOptions)}
                </Typography>
                {showCloseIcon && (
                    <Clickable
                        {...styles.closeClickable}
                        onClick={onClose}
                        data-test-selector="global_site_message_close"
                    >
                        <Icon {...styles.closeIcon} />
                    </Clickable>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: GlobalBannerMessage,
    definition: {
        group: "Header",
        displayName: "Global Banner Message",
        allowedContexts: ["Header"],
        fieldDefinitions: [
            {
                name: fields.backgroundColor,
                displayName: "Background Color",
                editorTemplate: "ColorPickerField",
                defaultValue: getSystemColor("common.ui10"),
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.message,
                displayName: "Global Message Content",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "General",
                placeholder: "Add your message here",
                isRequired: true,
            },
            {
                name: fields.showCloseIcon,
                displayName: " Show Close Icon",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
        ],
    },
};

export default widgetModule;
